// Subtitle text conversion for the download dialog: the library stores
// WebVTT tracks (see subtitle-map.js); standalone players want .srt.
// Pure functions — no DOM — so node tests cover them too.

/**
 * Convert WebVTT text to SubRip (srt). Best-effort and forgiving: a malformed
 * cue still lands in the output (players tolerate far more than spec says),
 * but a non-VTT payload (HTML error page, binary) throws so the caller can
 * surface "subtitles unavailable" instead of shipping a junk file.
 */
export function vttToSrt(vttText) {
  const text = String(vttText || "");
  if (!/^\uFEFF?WEBVTT/m.test(text)) throw new Error("Not a WebVTT file");

  const srtTime = (ts) => {
    // WebVTT: HH:MM:SS.mmm or MM:SS.mmm → srt: HH:MM:SS,mmm
    const m = /^\s*(?:(\d+):)?(\d{1,2}):(\d{2})[.,](\d{3})/.exec(ts);
    if (!m) return null;
    const h = m[1] ? String(Math.min(99, Number(m[1]))).padStart(2, "0") : "00";
    return `${h}:${m[2].padStart(2, "0")}:${m[3]},${m[4]}`;
  };

  // Strip the WEBVTT header block (any X-TIMESTAMP-MAP etc.) and split cues
  // on blank lines. Cue text may span several lines; NOTE/STYLE/REGION blocks
  // are dropped.
  const body = text.replace(/^\uFEFF?WEBVTT[^\n]*\n/, "").replace(/\n\n+/g, "\n\n");
  const out = [];
  let index = 0;
  for (const block of body.split(/\n\n/)) {
    const lines = block.split("\n").filter((l) => l.trim().length > 0);
    if (!lines.length) continue;
    if (/^(NOTE|STYLE|REGION)\b/.test(lines[0])) continue;
    // Find the timing line (may be preceded by an identifier line).
    let ti = lines.findIndex((l) => /\d{1,2}:\d{2}:\d{2}[.,]\d{3}\s+-->\s+\d{1,2}:\d{2}:\d{2}[.,]\d{3}/.test(l) || /\d{1,2}:\d{2}[.,]\d{3}\s+-->\s+\d{1,2}:\d{2}[.,]\d{3}/.test(l));
    if (ti === -1) continue;
    const [rawFrom, rest] = lines[ti].split("-->");
    const to = (rest || "").trim().split(/\s+/)[0];
    const from = srtTime(rawFrom);
    const toS = srtTime(to);
    if (!from || !toS) continue;
    const payload = lines.slice(ti + 1).filter((l) => !/^T[0-9]\b/.test(l)); // drop cue settings refs
    if (!payload.length) continue;
    index += 1;
    out.push(`${index}\n${from} --> ${toS}\n${payload.join("\n")}`);
  }
  return out.join("\n\n") + (out.length ? "\n" : "");
}

/** Format an ISO-639 code/name pair into a filename component (e.g. "Telugu"). */
export function subtitleFilename(label) {
  return `${String(label || "subtitles").replace(/[\\/:*?"<>|]+/g, "").trim() || "subtitles"}.srt`;
}
